/**
 * PURE. The face rig (creatures §C4): the DNA base face (`expr × exprInt`, `exprAsym`, eye-type biases), the eye
 * geometry shared by the mesh and the rig, and `faceToBones()` — face channels (`FACE_CHANNELS` order) → bone-local
 * deltas relative to the base face. The mesh is built WITH the base face baked in (lids, brows, mouth corners, gaze,
 * jaw), so the bind pose already shows the creature's solemn derp and a zero delta keeps it.
 */
import { FACE_CHANNELS, type BoneKey } from '../../shared';
import { EXPRESSIONS, NEUTRAL_FACE, type FaceValues } from '../../content/creature/expressions';
import type { CreatureSpec } from '../dna/resolve';
import type { BodyDims } from './skeleton';

/** Per-side base face values. */
export interface BaseFace {
  lid: [number, number]; pupil: [number, number]; browRaise: [number, number]; browAngle: [number, number];
  mouthCurve: number; mouthAsym: number; jaw: number; gazeYaw: [number, number]; gazePitch: number; earPose: number; noseWrinkle: number;
  omega: boolean; blush: number;
}

/** Eye-type biases (§A5.2: hooded/human heavy lids; madman/bulging wide). */
const LID_BIAS: Readonly<Record<string, number>> = { hooded: 0.2, human: 0.15, sad: 0.08, slits: 0.3, bulging: -0.05, owl: 0.08, beady: -0.1 };

const lerpF = (a: number, b: number, t: number): number => a + (b - a) * t;

/** DNA → base face (creatures §C4.2: `expr × exprInt`; `exprAsym` copies left → right at 60 % with the crooked face). */
export function baseFace(spec: CreatureSpec): BaseFace {
  const e: FaceValues = EXPRESSIONS[spec.f.expr] ?? EXPRESSIONS[0];
  const k = spec.v.exprInt;
  const asym = spec.v.exprAsym;
  const n = NEUTRAL_FACE;
  const lidBias = LID_BIAS[spec.o.eyes] ?? 0;
  const lidL = Math.max(0, Math.min(0.92, lerpF(n.lid, e.lid, k) + lidBias));
  const lidRraw = Math.max(0, Math.min(0.92, lerpF(n.lid, e.lidR ?? e.lid, k) + lidBias));
  const pupilBase = spec.v.pupilSize;
  const pupil = Math.max(0.2, Math.min(0.65, pupilBase + (e.pupil - n.pupil) * k));
  const raiseL = lerpF(0, e.browRaise, k) + spec.v.browHeight * 0.3;
  const raiseR = lerpF(0, e.browRaiseR ?? e.browRaise, k) + spec.v.browHeight * 0.3;
  const angL = lerpF(0, e.browAngle, k) + spec.v.browAngle / 20;
  const angR = lerpF(0, e.browAngleR ?? e.browAngle, k) + spec.v.browAngle / 20;
  const mix = (l: number, r: number): number => lerpF(r, l, 0.6 * asym);
  const cross = (e.crossEyed ?? 0) * k + (spec.o.eyes === 'crossed' ? 6 : 0);
  const yaw = lerpF(0, e.gazeYaw, k);
  return {
    lid: [lidL, mix(lidL, lidRraw)], pupil: [pupil, pupil],
    browRaise: [raiseL, mix(raiseL, raiseR)], browAngle: [angL, mix(angL, angR)],
    mouthCurve: lerpF(0, e.mouthCurve, k) + MOUTH_CURVE[spec.o.mouth] , mouthAsym: lerpF(0, e.mouthAsym ?? 0, k) + (spec.o.mouth === 'smirk' ? 0.6 : 0) + 0.3 * asym,
    jaw: lerpF(0, e.jaw, k) + (spec.o.mouth === 'o' ? 0.08 : 0), gazeYaw: [yaw - cross, yaw + cross], gazePitch: lerpF(0, e.gazePitch, k),
    earPose: lerpF(0, e.earPose ?? 0, k), noseWrinkle: lerpF(0, e.noseWrinkle ?? 0, k), omega: !!e.omega || spec.o.mouth === 'omega', blush: (e.blush ?? 0) * k,
  };
}

/** Mouth option → extra corner curve in degrees (Летописный −4° default is already the expression's). */
const MOUTH_CURVE: Readonly<Record<string, number>> = { chronicle: -2, straight: 0, smirk: 2, frown: -9, omega: 0, smile: 7, toad: -3, o: 0 };

/** Eye geometry of one side (raw units): sclera half-extents, iris radius, the lid cylinder and the eye frame. */
export interface EyeGeom {
  center: [number, number, number];
  /** Sclera half-width / half-height; iris radius; dome lift at the centre. */
  rx: number; ry: number; ri: number; dome: number;
  /** Eye frame: ex (local x, toward the creature's left for L), ez (facing). */
  ex: [number, number, number]; ez: [number, number, number];
  /** Lid cylinder radius around the axis through `lidPivot` along ex. */
  lidR: number; lidPivot: [number, number, number];
  /** Outer-corner tilt (deg, + = up) and the outline shape id. */
  tilt: number; shape: string;
}

const EYE_SHAPE: Readonly<Record<string, { sx: number; sy: number; dome: number; iris: number }>> = {
  almond: { sx: 1.18, sy: 0.82, dome: 0.32, iris: 0.8 }, round: { sx: 1.0, sy: 1.0, dome: 0.34, iris: 0.72 },
  hooded: { sx: 1.15, sy: 0.85, dome: 0.3, iris: 0.8 }, bulging: { sx: 1.05, sy: 1.05, dome: 0.75, iris: 0.62 },
  human: { sx: 1.25, sy: 0.78, dome: 0.28, iris: 0.72 }, crossed: { sx: 1.1, sy: 0.9, dome: 0.32, iris: 0.72 },
  mismatched: { sx: 1.05, sy: 0.95, dome: 0.34, iris: 0.72 }, slits: { sx: 1.3, sy: 0.55, dome: 0.25, iris: 0.95 },
  owl: { sx: 1.0, sy: 1.0, dome: 0.4, iris: 0.82 }, beady: { sx: 0.95, sy: 0.95, dome: 0.38, iris: 0.86 },
  sad: { sx: 1.15, sy: 0.85, dome: 0.3, iris: 0.78 }, cyclops: { sx: 1.1, sy: 1.0, dome: 0.4, iris: 0.72 },
};

/** Eye geometry for side +1 (left, +X) or −1 (right). `surfaceZ(x, y)` gives the skull front at the eye. */
export function eyeGeom(spec: CreatureSpec, d: BodyDims, side: 1 | -1, surfaceZ: number): EyeGeom {
  const shape = spec.flags.eyeStalks ? 'round' : spec.o.eyes;
  const sh = EYE_SHAPE[shape] ?? EYE_SHAPE.almond;
  let r = d.eyeR * (shape === 'owl' ? 1.15 : 1);
  if (shape === 'mismatched') r *= side > 0 ? 1.22 : 0.82;
  const rx = r * sh.sx, ry = r * sh.sy;
  const cyclops = shape === 'cyclops';
  const x = cyclops ? 0 : side * d.eyeX;
  const yaw = cyclops ? 0 : side * Math.asin(Math.min(0.9, Math.abs(d.eyeX) / Math.max(0.05, d.skullRx))) * 0.6;
  const ez: [number, number, number] = [Math.sin(yaw), 0, Math.cos(yaw)];
  const ex: [number, number, number] = [Math.cos(yaw), 0, -Math.sin(yaw)];
  const center: [number, number, number] = [x, d.eyeY, surfaceZ + 0.0025];
  const dome = ry * sh.dome;
  const lidR = ry * 1.5;
  const lidPivot: [number, number, number] = [center[0] - ez[0] * (lidR - dome - 0.0055), center[1], center[2] - ez[2] * (lidR - dome - 0.0055)];
  const tilt = side * 0 + spec.v.eyeTilt + (shape === 'sad' ? -12 : 0);
  return { center, rx, ry, ri: ry * sh.iris, dome, ex, ez, lidR, lidPivot, tilt, shape };
}

/** Lid edge height (relative to the eye centre) for a cover value (0 = at the sclera top, 1 = shut; 0.45 ≈ 45 % of the
 *  iris, art §5.5), as the lid angle from +Y about ex. */
export function lidAngle(g: EyeGeom, cover: number): number {
  const c = Math.max(0, Math.min(1, cover));
  const yEdge = g.ry * (1.02 - 2.06 * c);
  return Math.acos(Math.max(-0.999, Math.min(0.999, yEdge / g.lidR)));
}

/** Gaze offset of the iris/pupil group in the eye plane for (yaw, pitch) degrees (±0.32 sclera half-height at 25°). */
export function gazeOffset(g: EyeGeom, yawDeg: number, pitchDeg: number): [number, number] {
  const k = 0.32 * g.ry;
  return [Math.max(-1, Math.min(1, yawDeg / 25)) * k * 1.25, Math.max(-1, Math.min(1, pitchDeg / 25)) * k];
}

export interface FaceRigInfo { eyes: [EyeGeom, EyeGeom]; base: BaseFace; mouthHalfW: number; norm: number }

/**
 * Face channels (FACE_CHANNELS order; creatures §C4.1) → bone-local keys relative to the baked base face, plus the
 * `CreatureLook.face` values (pupilScaleL, pupilScaleR, blush, tear — pupil size goes to the shader, GDD C106).
 * Rotations are Euler degrees (YXZ, §B1), translations in H (normalized). Unknown/missing channels = the base value.
 */
export function faceToBones(info: FaceRigInfo, ch: Partial<Record<(typeof FACE_CHANNELS)[number], number>>): { bones: Partial<Record<string, BoneKey>>; look: [number, number, number, number] } {
  const b = info.base;
  const n = info.norm;
  const out: Partial<Record<string, BoneKey>> = {};
  const look: [number, number, number, number] = [1, 1, ch.blush ?? b.blush, ch.tear ?? 0];
  const get = (k: (typeof FACE_CHANNELS)[number], base: number): number => ch[k] ?? base;
  const sides: Array<[1 | -1, 'L' | 'R', 0 | 1]> = [[1, 'L', 0], [-1, 'R', 1]];
  for (const [sgn, S, i] of sides) {
    const g = info.eyes[i];
    const lid = get(i === 0 ? 'lidL' : 'lidR', b.lid[i]);
    const dLid = (lidAngle(g, lid) - lidAngle(g, b.lid[i])) * (180 / Math.PI);
    out[`lid_${S}`] = { r: [dLid, 0, 0] };
    const raise = get(i === 0 ? 'browRaiseL' : 'browRaiseR', b.browRaise[i]) - b.browRaise[i];
    const ang = get(i === 0 ? 'browAngleL' : 'browAngleR', b.browAngle[i]) - b.browAngle[i];
    out[`brow_${S}`] = { t: [0, raise * 0.012 * n, 0], r: [0, 0, sgn * ang * 20] };
    const [gx0, gy0] = gazeOffset(g, b.gazeYaw[i], b.gazePitch);
    const [gx1, gy1] = gazeOffset(g, get('gazeYaw', b.gazeYaw[i]), get('gazePitch', b.gazePitch));
    const pupil = get('pupil', b.pupil[i]);
    out[`eye_${S}`] = { t: [(gx1 - gx0) * n, (gy1 - gy0) * n, 0] };
    look[i] = pupil / b.pupil[i];
    const ear = get('earPose', b.earPose) - b.earPose;
    out[`ear_${S}_01`] = { r: [-ear * 25, 0, sgn * ear * 15] };
  }
  const curve = get('mouthCurve', b.mouthCurve);
  const dy = (Math.tan((curve * Math.PI) / 180) - Math.tan((b.mouthCurve * Math.PI) / 180)) * info.mouthHalfW * n;
  out.mouth = { t: [0, dy, 0], r: [0, 0, (get('mouthAsym', b.mouthAsym) - b.mouthAsym) * 12] };
  out.jaw = { r: [(get('jawOpen', b.jaw) - b.jaw) * 35, 0, 0] };
  out.nose = { t: [0, (get('noseWrinkle', b.noseWrinkle) - b.noseWrinkle) * 0.004 * n, 0] };
  return { bones: out, look };
}
